import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { type ThemeConfig, defaultTheme, getModeSurfaceDefaults } from '@/types/theme'

interface ThemeState {
  theme: ThemeConfig

  // Actions
  setTheme: (theme: Partial<ThemeConfig>) => void
  setMode: (mode: 'dark' | 'light') => void
  setBrandPrimary: (color: string) => void
  setBrandSecondary: (color: string) => void
  setCompanyName: (name: string) => void
  setLogoUrl: (url: string | null) => void
  resetTheme: () => void
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: defaultTheme,

      setTheme: (partial) =>
        set((state) => {
          const modeChanged = partial.mode && partial.mode !== state.theme.mode
          const defaults = modeChanged ? getModeSurfaceDefaults(partial.mode!) : {}
          return {
            theme: {
              ...state.theme,
              ...defaults,
              ...partial,
            },
          }
        }),

      setMode: (mode) =>
        set((state) => ({
          theme: {
            ...state.theme,
            mode,
            ...getModeSurfaceDefaults(mode),
          },
        })),

      setBrandPrimary: (color) =>
        set((state) => ({
          theme: { ...state.theme, brandPrimary: color },
        })),

      setBrandSecondary: (color) =>
        set((state) => ({
          theme: { ...state.theme, brandSecondary: color },
        })),

      setCompanyName: (name) =>
        set((state) => ({
          theme: { ...state.theme, companyName: name },
        })),

      setLogoUrl: (url) =>
        set((state) => ({
          theme: { ...state.theme, logoUrl: url },
        })),

      resetTheme: () => set({ theme: defaultTheme }),
    }),
    {
      name: 'crm-theme',
      version: 2,
      migrate: (persisted, version) => {
        const state = persisted as { theme?: ThemeConfig }
        if (version >= 2 || !state.theme) return persisted as ThemeState
        return {
          ...state,
          theme: {
            ...state.theme,
            mode: 'light' as const,
            ...getModeSurfaceDefaults('light'),
          },
        }
      },
    },
  ),
)

/** Tema vindo do servidor não troca o modo que a pessoa já escolheu. */
export function mergeThemeKeepingMode(incoming: ThemeConfig, chosenMode: ThemeConfig['mode']): ThemeConfig {
  if (incoming.mode === chosenMode) return incoming
  const surfaces = getModeSurfaceDefaults(chosenMode)
  return {
    ...incoming,
    ...surfaces,
    mode: chosenMode,
    brandPrimary: incoming.brandPrimary,
    brandPrimaryHover: incoming.brandPrimaryHover,
    brandPrimaryMuted: incoming.brandPrimaryMuted,
    navActiveColor: incoming.navActiveColor,
    navActiveMuted: incoming.navActiveMuted,
  }
}
